import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star, ArrowUpRight, Code2, Globe, Smartphone, Plug, Cloud, Workflow, RefreshCw, Brain, Box } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { AVAIL, api } from "@/lib/api";

export const ICONS = { code: Code2, globe: Globe, smartphone: Smartphone, plug: Plug, cloud: Cloud, workflow: Workflow, refresh: RefreshCw, brain: Brain };
export const ServiceIcon = ({ name, className = "h-5 w-5" }) => {
  const I = ICONS[name] || Box;
  return <I className={className} />;
};

export const SectionHead = ({ eyebrow, title, sub, dark, right }) => (
  <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
    <div className="max-w-2xl">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 className={`mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
      {sub && <p className={`mt-4 text-base md:text-lg leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{sub}</p>}
    </div>
    {right}
  </div>
);

export const Provisional = ({ title, text, testid = "provisional-block", dark }) => (
  <div data-testid={testid} className={`rounded-2xl border-2 border-dashed p-8 ${dark ? "border-white/15 text-slate-400" : "border-slate-300 bg-slate-50/60 text-slate-500"}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-600">Contenido provisional · pendiente de aprobación</p>
    {title && <p className={`mt-2 font-display text-lg font-semibold ${dark ? "text-white" : "text-slate-800"}`}>{title}</p>}
    {text && <p className="mt-1 text-sm">{text}</p>}
  </div>
);

export function Metrics({ items }) {
  if (!items.length)
    return <Provisional testid="metrics-placeholder" title="Indicadores de confianza" text="Los indicadores (años de experiencia, proyectos, países, clientes e industrias) se mostrarán cuando sean verificados desde el panel administrativo." />;
  return (
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2" style={{ gridTemplateColumns: window.innerWidth >= 768 ? `repeat(${items.length}, minmax(0, 1fr))` : undefined }} data-testid="metrics-grid">
      {items.map((m) => (
        <div key={m.id} className="bg-white p-6 md:p-8" data-testid={`metric-${m.id}`}>
          <p className="font-display text-3xl md:text-4xl font-bold text-slate-900">{m.value}{m.suffix}</p>
          <p className="mt-2 text-sm text-slate-500">{m.label}</p>
          {m.detail && <p className="mt-1 text-xs text-slate-400">{m.detail}</p>}
        </div>
      ))}
    </div>
  );
}

export function Partners({ items }) {
  if (!items.length) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="partners-grid">
      {items.map((p) => {
        const Tag = p.url ? "a" : "div";
        return (
          <Tag key={p.id} {...(p.url ? { href: p.url, target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={`partner-${p.id}`}
            className="card-cc card-cc-hover group flex items-center gap-4 !p-6">
            {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-12 w-24 object-contain" />
              : <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-navy font-display font-bold text-white">{p.name?.slice(0, 2).toUpperCase()}</span>}
            <div className="flex-1">
              <p className="font-display text-lg font-semibold text-slate-900">{p.name}</p>
              {p.description && <p className="text-sm text-slate-500">{p.description}</p>}
            </div>
            {p.url && <ArrowUpRight className="h-5 w-5 text-slate-400 transition-colors group-hover:text-brand" />}
          </Tag>
        );
      })}
    </div>
  );
}

export function ClientsCarousel({ clients }) {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);
  const placeholder = !clients.length;
  const base = placeholder ? [1, 2, 3, 4, 5, 6].map((i) => ({ id: `ph${i}` })) : clients;
  const loop = base.length > 2;
  const items = loop ? [...base, ...base] : base;
  useEffect(() => {
    if (paused || !loop || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    const id = setInterval(() => {
      if (!el) return;
      const half = el.scrollWidth / 2;
      el.scrollLeft = el.scrollLeft >= half ? el.scrollLeft - half : el.scrollLeft + 1;
    }, 25);
    return () => clearInterval(id);
  }, [paused, loop]);
  const scroll = (d) => {
    const el = ref.current;
    if (!el) return;
    if (loop && d < 0 && el.scrollLeft < 260) el.scrollLeft += el.scrollWidth / 2;
    el.scrollBy({ left: d * 260, behavior: "smooth" });
  };
  return (
    <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent" />
      <div ref={ref} tabIndex={0} role="region" aria-label="Carrusel de logotipos de clientes" data-testid="clients-carousel"
        onKeyDown={(e) => { if (e.key === "ArrowRight") scroll(1); if (e.key === "ArrowLeft") scroll(-1); }}
        className="no-scrollbar flex gap-4 overflow-x-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cc-blue)] rounded-xl">
        {items.map((c, i) => placeholder ? (
          <div key={`${c.id}-${i}`} aria-hidden={i >= base.length} data-testid={i < base.length ? "client-logo-placeholder" : undefined}
            className="grid h-24 w-56 shrink-0 place-items-center rounded-xl border-2 border-dashed border-slate-300 bg-white text-center font-mono text-[10px] uppercase tracking-widest text-slate-400">
            Logo cliente<br />pendiente de autorización
          </div>
        ) : (
          <a key={`${c.id}-${i}`} href={c.url || undefined} target="_blank" rel="noopener noreferrer" aria-hidden={i >= base.length} tabIndex={i >= base.length ? -1 : undefined}
            data-testid={i < base.length ? `client-logo-${c.id}` : undefined} title={c.name}
            className={`group grid h-24 w-56 shrink-0 place-items-center rounded-xl border bg-white px-6 transition-[border-color,box-shadow] hover:shadow-lg ${c.featured ? "border-[color:var(--cc-blue)]" : "border-slate-200"}`}>
            {c.logo_url ? <img src={c.logo_url} alt={c.name} loading="lazy" className="max-h-16 max-w-full object-contain" />
              : <span className="font-display font-semibold text-slate-700">{c.name}</span>}
          </a>
        ))}
      </div>
      </div>
      {placeholder && <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600" data-testid="clients-placeholder">Contenido provisional · los logotipos se cargan desde Admin → Clientes (logotipos)</p>}
      <div className="mt-6 flex gap-2">
        <button onClick={() => scroll(-1)} aria-label="Anterior" data-testid="clients-prev" className="rounded-full border border-slate-300 p-2 hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
        <button onClick={() => scroll(1)} aria-label="Siguiente" data-testid="clients-next" className="rounded-full border border-slate-300 p-2 hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export const TestimonialCard = ({ t }) => (
  <figure className="card-cc h-full flex flex-col" data-testid={`testimonial-${t.id}`}>
    {t.rating > 0 && <div className="flex gap-0.5 text-amber-400">{Array.from({ length: Number(t.rating) }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>}
    <blockquote className="mt-4 flex-1 text-slate-700 leading-relaxed">“{t.text}”</blockquote>
    <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
      {t.logo_url ? <img src={t.logo_url} alt={t.company || ""} className="h-9 w-auto max-w-[140px] object-contain" data-testid="testimonial-logo" />
        : t.photo_url ? <img src={t.photo_url} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 font-semibold text-slate-600">{t.name?.[0]}</span>}
      <div>
        <p className="text-sm font-semibold text-slate-900">{t.name}</p>
        <p className="text-xs text-slate-500">{[t.role, t.company].filter(Boolean).join(" · ")}</p>
      </div>
    </figcaption>
  </figure>
);

export function Testimonials({ items }) {
  if (!items.length)
    return <Provisional testid="testimonials-placeholder" title="Testimonios verificados" text="Esta sección mostrará únicamente testimonios reales, verificables y autorizados por los clientes, aprobados desde el panel administrativo." />;
  return (
    <Carousel opts={{ align: "start" }} className="px-1" data-testid="testimonials-carousel">
      <CarouselContent>
        {items.map((t) => (
          <CarouselItem key={t.id} className="md:basis-1/2 lg:basis-1/3"><TestimonialCard t={t} /></CarouselItem>
        ))}
      </CarouselContent>
      <div className="mt-6 flex gap-2 [&>button]:static [&>button]:translate-y-0">
        <CarouselPrevious data-testid="testimonials-prev" /><CarouselNext data-testid="testimonials-next" />
      </div>
    </Carousel>
  );
}

export const Technologies = ({ items }) => (
  <div className="flex flex-wrap gap-3" data-testid="technologies-list">
    {items.map((t) => (
      <span key={t.id} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-[border-color,color] hover:border-[color:var(--cc-blue)] hover:text-brand">
        {t.logo_url ? <img src={t.logo_url} alt="" className="h-5 w-5 object-contain" /> : <span className="h-1.5 w-1.5 rounded-full bg-brand" />}
        {t.name}
      </span>
    ))}
  </div>
);

export const ProductCard = ({ p }) => {
  const a = AVAIL[p.availability] || AVAIL.demo_only;
  return (
    <Link to={`/soluciones/${p.slug}`} data-testid={`product-card-${p.slug}`} className="card-cc card-cc-hover group flex flex-col">
      <div className="flex items-start justify-between">
        {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-12 w-12 rounded-xl object-contain" />
          : <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy font-display text-lg font-bold text-white">{p.name?.[0]}</span>}
        <ArrowUpRight className="h-5 w-5 text-slate-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand" />
      </div>
      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{p.category}</p>
      <h3 className="mt-1 font-display text-xl font-semibold text-slate-900">{p.name}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.short_desc}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${a.cls}`}>{a.label}</span>
        {p.provisional && <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">Provisional</span>}
        {p.status !== "published" && <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">Borrador (vista admin)</span>}
      </div>
    </Link>
  );
};

export const CtaBand = () => (
  <section className="wrap py-16 md:py-24">
    <div className="relative overflow-hidden rounded-3xl bg-navy px-8 py-14 md:px-16 noise">
      <div className="absolute inset-0 grid-bg" />
      <div className="relative flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div className="max-w-xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-signal">Hablemos de su operación</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">¿Listo para transformar sus procesos con tecnología?</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/demo" className="btn-cc" data-testid="cta-band-demo">Solicitar una demostración</Link>
          <Link to="/contacto" className="btn-ghost-cc border-white/20 text-white hover:bg-white/10" data-testid="cta-band-contact">Contactar</Link>
        </div>
      </div>
    </div>
  </section>
);

export function useList(path, deps = []) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(path).then((r) => alive && setData(r.data)).catch(() => alive && setData([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  return data;
}
